import tenantDefaults from '../config/organization.json';
import tenantResolver from '../services/tenantResolver';
import Logger from './logger';
import APICall, { HTTP_METHODS } from '../services/apiservices';

/**
 * Tenant Configuration Utility
 * Provides centralized access to tenant-specific settings and branding
 * Supports dynamic multi-tenant configuration loading from bitsmith.tenants
 */
class TenantConfig {
  constructor() {
    this.config = tenantDefaults.organization; // Fallback static config
    this.dynamicConfig = null; // Dynamic config from API
    this.isLoaded = false;
  }

  /**
   * Initialize configuration by fetching tenant info from API.
   * Falls back to static config on error.
   */
  async initialize() {
    try {
      // Ensure tenant resolver is initialized
      if (!tenantResolver.isInitialized()) {
        tenantResolver.initialize();
      }

      const slug = tenantResolver.getCurrentTenantSlug();

      // Check if user is authenticated
      const accessToken = localStorage.getItem("accessToken");
      
      let response;
      if (accessToken) {
        // User is logged in - use authenticated endpoint for full data
        const cacheBuster = Date.now();
        response = await APICall(`/tenant/current?_t=${cacheBuster}`, null, HTTP_METHODS.GET);
        Logger.info('Fetched tenant config from authenticated endpoint');
      } else {
        // User not logged in - use public endpoint
        const cacheBuster = Date.now();
        response = await APICall(`/tenant/public?_t=${cacheBuster}`, null, HTTP_METHODS.GET, false);
        Logger.info('Fetched tenant config from public endpoint');
      }

      const tenantData = response?.data || response;
      if (tenantData && typeof tenantData === 'object') {
        const mapped = this.mapTenantToConfig({
          ...tenantData,
          slug: tenantData.slug || slug,
          id: tenantData.slug || slug
        });
        this.setConfig(mapped);
        Logger.info('Tenant config initialized with dynamic configuration', { slug });
        return this.getActiveConfig();
      }

      // Fallback to static config if unexpected response
      this.isLoaded = true;
      Logger.warn('Tenant config: unexpected response, using static config');
      return this.getActiveConfig();
    } catch (error) {
      Logger.error('Failed to fetch tenant config, using static config:', error);
      this.isLoaded = true;
      return this.config;
    }
  }

  /**
   * Map tenant data to internal config format
   */
  mapTenantToConfig(tenant) {
    return {
      id: tenant._id || tenant.id,
      name: tenant.name,
      shortName: tenant.shortName,
      slug: tenant.slug,
      logoFileId: tenant.logoFileId || null,
      logoURL: tenant.logoURL || null,
      branding: tenant.branding || this.config.branding,
      contact: tenant.contact || this.config.contact,
      social: tenant.social || this.config.social,
      legal: tenant.legal || this.config.legal,
      settings: tenant.settings || this.config.settings,
      management: tenant.management || this.config.management,
      documents: tenant.documents || this.config.documents,
      formats: tenant.formats || this.config.formats,
      descriptions: tenant.descriptions || this.config.descriptions,
      metadata: tenant.metadata || this.config.metadata,
      templates: tenant.templates || this.config.templates,
      sidebarMenu: tenant.sidebarMenu || this.config.sidebarMenu,
      api: tenant.api || this.config.api,
    };
  }

  /**
   * Get active configuration (dynamic if available, otherwise static)
   */
  getActiveConfig() {
    return this.dynamicConfig || this.config;
  }

  /**
   * Check if dynamic configuration is loaded
   */
  isDynamicConfigLoaded() {
    return this.isLoaded && this.dynamicConfig !== null;
  }

  /**
   * Get tenant ID
   */
  getTenantId() {
    const config = this.getActiveConfig();
    return config.id || '649bff0f87a35d7bececa3d8';
  }

  /**
   * @deprecated Use getTenantId() instead
   */
  getOrganizationId() {
    return this.getTenantId();
  }

  /**
   * Get tenant basic information
   */
  getBasicInfo() {
    const config = this.getActiveConfig();
    return {
      id: config.id,
      name: config.name,
      shortName: config.shortName,
      slug: config.slug,
    };
  }

  /**
   * Get tenant contact information
   */
  getContactInfo() {
    const config = this.getActiveConfig();
    return config.contact;
  }

  /**
   * Get tenant branding assets
   */
  getBranding() {
    const config = this.getActiveConfig();
    return config.branding;
  }

  /**
   * Get social media links
   */
  getSocialLinks() {
    const config = this.getActiveConfig();
    const social = { ...config.social };
    if (social.whatsapp && social.whatsapp.includes('{mobile}')) {
      social.whatsapp = social.whatsapp.replace('{mobile}', config.contact.mobile);
    }
    return social;
  }

  /**
   * Get formatted numbers (AMC, Employee codes, etc.)
   */
  getFormattedNumber(type, data) {
    const config = this.getActiveConfig();
    const formats = config.formats;
    
    switch (type) {
      case 'amcNumber':
        return formats.amcNumber.replace('{amcNo}', data.amcNo?.toString().padStart(4, '0') || '');
      case 'employeeCode':
        return formats.employeeCode.replace('{employeeCode}', data.employeeCode?.toString().padStart(4, '0') || '');
      case 'reportCode':
        return formats.reportCode
          .replace('{yearString}', this.getYearString())
          .replace('{reportCode}', data.reportCode || '');
      case 'quotationNumber':
        return formats.quotationNumber
          .replace('{yearString}', this.getYearString())
          .replace('{quotationNumber}', data.quotationNumber?.toString().padStart(4, '0') || '');
      case 'certificateNumber':
        return formats.certificateNumber
          .replace('{yearString}', this.getYearString())
          .replace('{certificateNumber}', data.certificateNumber || '');
      default:
        return '';
    }
  }

  /**
   * Get current year string in format "YY-YY"
   */
  getYearString() {
    const currentYear = new Date().getFullYear();
    return `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
  }

  /**
   * Get tenant metadata for HTML head
   */
  getMetadata() {
    const config = this.getActiveConfig();
    return {
      ...config.metadata,
      title: 'Inferno - The Most Advanced Fire Safety Management System',
      description: config.metadata?.description || 'The Most Advanced Fire Safety Management Software'
    };
  }

  /**
   * Get tenant descriptions
   */
  getDescriptions() {
    const config = this.getActiveConfig();
    return config.descriptions;
  }

  /**
   * Get legal information
   */
  getLegalInfo() {
    const config = this.getActiveConfig();
    return config.legal;
  }

  /**
   * Get management information
   */
  getManagement() {
    const config = this.getActiveConfig();
    return config.management;
  }

  /**
   * Get API configuration
   */
  getApiConfig() {
    const config = this.getActiveConfig();
    return {
      ...config.api,
      tenantEndpoint: config.api.tenantEndpoint.replace('{tenantId}', config.id)
    };
  }

  /**
   * Get offer letter terms with placeholder support
   */
  getOfferLetterTerms(data = {}) {
    const config = this.getActiveConfig();
    return config.templates.offerLetterTerms.map(term => {
      let processedTerm = term;
      processedTerm = processedTerm.replace(/\{\{organizationShortName\}\}/g, config.shortName);
      processedTerm = processedTerm.replace(/\{\{tenantShortName\}\}/g, config.shortName);
      
      Object.keys(data).forEach(key => {
        const placeholder = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        processedTerm = processedTerm.replace(placeholder, data[key] || '');
      });
      
      return processedTerm;
    });
  }

  /**
   * Get tenant data for exports (surveys, AMCs, etc.)
   */
  getExportData() {
    const config = this.getActiveConfig();
    return {
      name: config.name,
      address: config.contact.address.full,
      mobile: config.contact.mobile,
      email: config.contact.email,
    };
  }

  /**
   * Get PDF footer information
   */
  getPdfFooterInfo() {
    const config = this.getActiveConfig();
    return {
      organization: config.name,
      office: config.contact.address.office,
      area: config.contact.address.area,
      authorizedSignature: 'Authorised Signature',
      director: config.management.director
    };
  }

  /**
   * Set tenant configuration (for multi-tenant switching)
   */
  setConfig(newConfig) {
    this.dynamicConfig = newConfig;
    this.isLoaded = true;
  }

  /**
   * Get full configuration
   */
  getFullConfig() {
    return this.getActiveConfig();
  }

  /**
   * Get logo path or URL
   */
  getLogo(type = 'primary') {
    const config = this.getActiveConfig();
    if (config.logoURL) {
      return config.logoURL;
    }
    if (config.branding?.logo) {
      return config.branding.logo[type] || config.branding.logo.primary;
    }
    return '/inferno-logo.png';
  }

  /**
   * Get favicon path
   */
  getFavicon() {
    const config = this.getActiveConfig();
    if (config.logoURL) {
      return config.logoURL;
    }
    return config.branding?.favicon || '/inferno-logo.png';
  }

  /**
   * Update HTML document title and favicon
   */
  updateDocumentMeta() {
    const config = this.getActiveConfig();
    if (typeof document !== 'undefined') {
      document.title = config.metadata.title;
      
      const faviconLink = document.querySelector("link[rel='icon']") || document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.href = config.branding.favicon;
      if (!document.querySelector("link[rel='icon']")) {
        document.head.appendChild(faviconLink);
      }

      const metaDescription = document.querySelector("meta[name='description']") || document.createElement('meta');
      metaDescription.name = 'description';
      metaDescription.content = config.metadata.description;
      if (!document.querySelector("meta[name='description']")) {
        document.head.appendChild(metaDescription);
      }
    }
  }
}

// Create singleton instance
const tenantConfig = new TenantConfig();

export default tenantConfig;

// New naming convention exports
export const initializeTenantConfig = () => tenantConfig.initialize();
export const getTenantId = () => tenantConfig.getTenantId();
export const getTenantConfig = () => tenantConfig;
export const getBasicInfo = () => tenantConfig.getBasicInfo();
export const getContactInfo = () => tenantConfig.getContactInfo();
export const getBranding = () => tenantConfig.getBranding();
export const getSocialLinks = () => tenantConfig.getSocialLinks();
export const getFormattedNumber = (type, data) => tenantConfig.getFormattedNumber(type, data);
export const getExportData = () => tenantConfig.getExportData();
export const getPdfFooterInfo = () => tenantConfig.getPdfFooterInfo();
export const getLogo = (type) => tenantConfig.getLogo(type);
export const getFavicon = () => tenantConfig.getFavicon();
export const getMetadata = () => tenantConfig.getMetadata();
export const getYearString = () => tenantConfig.getYearString();
export const getApiConfig = () => tenantConfig.getApiConfig();
export const isDynamicConfigLoaded = () => tenantConfig.isDynamicConfigLoaded();

// Backward compatibility exports (deprecated - use new names above)
export const initializeOrganizationConfig = initializeTenantConfig;
export const getOrganizationId = getTenantId;
export const getOrganizationConfig = getTenantConfig;
